import type { InitOptions } from 'i18next';
import type { Theme, Components } from '@mui/material/styles';

// MUI Core Locales
import { itIT as itITCore, enUS as enUSCore } from '@mui/material/locale';
// MUI Date Pickers Locales
import { enUS as enUSDate, itIT as itITDate } from '@mui/x-date-pickers/locales';

// ----------------------------------------------------------------------
// Import translation files directly
import enTranslations from './langs/en/en.json';
import itTranslations from './langs/it/it.json';

// Supported languages
export const supportedLngs = ['en', 'it'] as const;
export type LangCode = (typeof supportedLngs)[number];

// Fallback and default namespace
export const fallbackLng: LangCode = 'it';

// Storage config
export const storageConfig = {
  cookie: { key: 'i18next', autoDetection: false },
  localStorage: { key: 'i18nextLng', autoDetection: false },
} as const;

// ----------------------------------------------------------------------

/**
 * @countryCode https://flagcdn.com/en/codes.json
 * @adapterLocale https://github.com/iamkun/dayjs/tree/master/src/locale
 * @numberFormat https://simplelocalize.io/data/locales/
 */

export type LangOption = {
  value: LangCode;
  label: string;
  countryCode: string;
  adapterLocale?: string;
  numberFormat: { code: string; currency: string };
  systemValue?: { components: Components<Theme> };
};

export const allLangs: LangOption[] = [
  {
    value: 'en',
    label: 'English',
    countryCode: 'GB',
    adapterLocale: 'en',
    numberFormat: { code: 'en-US', currency: 'EUR' },
    systemValue: {
      components: { ...enUSCore.components, ...enUSDate.components },
    },
  },
  {
    value: 'it',
    label: 'Italian',
    countryCode: 'IT',
    adapterLocale: 'it',
    numberFormat: { code: 'en-IT', currency: 'EUR' },
    systemValue: {
      components: { ...itITCore.components, ...itITDate.components },
    },
  },
];
export const defaultLang = allLangs.find((lang) => lang.value === fallbackLng)!;

// ----------------------------------------------------------------------

/**
 * Which section each flattened namespace would come from.
 *
 * Exported so the guard test asserts the real walk instead of a copy of it.
 */
export const namespaceOwners = (
  translations: Record<string, unknown>,
): Record<string, string[]> => {
  const owners: Record<string, string[]> = {};
  Object.entries(translations).forEach(([sectionKey, sectionValue]) => {
    if (sectionKey !== 'common' && typeof sectionValue === 'object' && sectionValue !== null) {
      Object.keys(sectionValue as Record<string, unknown>).forEach((pageKey) => {
        (owners[pageKey] ||= []).push(sectionKey);
      });
    }
  });
  return owners;
};

// Create resources object for i18next - flat namespace structure
export const createResources = (translations: Record<string, unknown>) => {
  const resources: Record<string, any> = {};

  // Add common translations
  if (translations.common) {
    resources.common = translations.common;
  }

  // Dynamically add all nested sections as flat namespaces.
  //
  // Flattening means two sections that happen to share a child name collapse
  // into one, and the loser's keys become unreachable — silently, since both
  // spellings are perfectly valid JSON. That is not hypothetical: `orders` and
  // `products` both carried a `producersPage`, `products` won, and 17 keys of
  // the other rendered as raw enum text on an admin screen in production.
  //
  // `namespaceOwners` is exported and used by
  // `__tests__/locale-namespaces.test.ts`, so the guard asserts THIS walk rather
  // than a second copy of the rule that could drift from it. The duplicate-key
  // guard beside it reads the files as text and cannot see this class at all.
  const owners = namespaceOwners(translations);

  Object.entries(translations).forEach(([sectionKey, sectionValue]) => {
    if (sectionKey !== 'common' && typeof sectionValue === 'object' && sectionValue !== null) {
      Object.entries(sectionValue).forEach(([pageKey, pageValue]) => {
        resources[pageKey] = pageValue;
      });
    }
  });

  const collisions = Object.entries(owners).filter(([, sections]) => sections.length > 1);
  if (collisions.length) {
    const detail = collisions
      .map(([ns, sections]) => `"${ns}" from ${sections.join(' and ')}`)
      .join('; ');
    const message =
      `Locale namespace collision: ${detail}. Flattened namespaces must be unique, ` +
      `or the losing section's keys become unreachable.`;

    // Throwing only under test. This runs at module evaluation, before React
    // mounts, so a throw in dev is not a loud warning — it is a white page with
    // no error boundary and no HMR recovery until the JSON is fixed. The test
    // is the gate that stops a collision reaching anyone; the console is how a
    // developer finds out without losing their app.
    if (import.meta.env?.MODE === 'test') {
      throw new Error(message);
    }
    // eslint-disable-next-line no-console
    console.error(message);
  }

  return resources;
};

const resources = {
  en: createResources(enTranslations),
  it: createResources(itTranslations),
} as const;

// Dynamically generate namespace list
const generateNamespaces = (): string[] => {
  const namespaces = new Set<string>();

  // Extract all page keys from all language resources to ensure completeness
  Object.values(resources).forEach((langResources) => {
    Object.keys(langResources).forEach((key) => {
      namespaces.add(key);
    });
  });

  return Array.from(namespaces);
};

export function i18nOptions(lang = fallbackLng): InitOptions {
  const generatedNamespaces = generateNamespaces();
  return {
    // debug: true,
    supportedLngs,
    fallbackLng,
    lng: lang,
    resources,
    defaultNS: 'common', // Use 'common' as deterministic default namespace
    ns: generatedNamespaces, // Dynamically generated
    interpolation: {
      escapeValue: false, // React already escapes
    },
  };
}

export function getCurrentLang(lang?: string): LangOption {
  const fallbackLang = allLangs.find((l) => l.value === fallbackLng) ?? allLangs[0];

  if (!lang) {
    return fallbackLang;
  }

  return allLangs.find((l) => l.value === lang) ?? fallbackLang;
}
